import {
  BatchRulesRepository,
  CheckResultEntry,
  EMediaExistingHandling,
} from "../../../domain/batch-rules-repository";
import { BatchRule, BatchRuleType } from "../../../domain/batch-rule";
import { HttpService } from "../../http/http";
import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import moment from "moment";
import { TenantStorageOption } from "../../../domain/tenant-storage-option";

function toMediaFileType(ruleType: BatchRuleType) {
  switch (ruleType) {
    case BatchRuleType.Video:
      return "MainVideo";
    default:
      return BatchRuleType[ruleType];
  }
}

function getLanguageCode(rule: BatchRule) {
  switch (rule.type) {
    case BatchRuleType.Subtitles:
      return rule.subtitlesLanguage;
    case BatchRuleType.Audio:
      return rule.audioLanguage;
  }
  return undefined;
}

@Injectable()
export class BatchRulesRepositoryImpl implements BatchRulesRepository {
  constructor(private httpService: HttpService) {}

  ruleAdded = new Subject<BatchRule>();
  ruleUpdated = new Subject<BatchRule>();

  getRules(): Promise<BatchRule[]> {
    const batchRuleTypes = {
      MainVideo: BatchRuleType.Video,
      Audio: BatchRuleType.Audio,
      Subtitles: BatchRuleType.Subtitles,
      Packshot: BatchRuleType.Packshot,
      Trailer: BatchRuleType.Trailer,
    };
    return this.httpService
      .get("MediaAssociation/GetAllRules")
      .then((rules) => {
        return rules.map(
          (rule) =>
            new BatchRule(
              rule.RuleId,
              rule.RuleName,
              rule.RuleFormat,
              rule.IdFormat,
              batchRuleTypes[rule.MediaFileType],
              moment(rule.DateCreated),
              rule.LanguageCode,
              rule.LanguageCode,
              rule.SubtitlesFPS || 0,
              rule.SubtitlesOffset || 0,
            ),
        );
      });
  }

  addRule(rule: BatchRule): Promise<number> {
    return this.upsertRule(rule).then((ruleId) => {
      rule.id = ruleId;
      rule.dateCreated = moment();
      this.ruleAdded.next(rule);
      return ruleId;
    });
  }

  updateRule(rule: BatchRule, detachRelatedSubtitles?: boolean): Promise<void> {
    return this.upsertRule(rule, detachRelatedSubtitles).then(() => {
      this.ruleUpdated.next(rule);
    });
  }

  deleteRules(rules: BatchRule[]): Promise<any> {
    let promises = rules.map((x) => {
      return this.httpService.delete(`MediaAssociation/DeleteRule/${x.id}`);
    });

    return Promise.all(promises);
  }

  check(
    path: string,
    storageOption: TenantStorageOption,
    selectedRuleIds: number[],
  ): Promise<CheckResultEntry[]> {
    return this.httpService
      .post("MediaAssociation/GetFilesList", {
        Path: path,
        StorageId: storageOption.id,
        SelectedRuleIds: selectedRuleIds,
      })
      .then((result) => {
        return this.createSummaryResult(result);
      });
  }

  go(
    path: string,
    storageOption: TenantStorageOption,
    deadlineOffsetHours: number,
    existingMediaHandlingPolicy: EMediaExistingHandling,
    selectedRuleIds: number[],
  ): Promise<void> {
    return this.httpService.post("MediaAssociation/ApplyRules", {
      Path: path,
      StorageId: storageOption.id,
      DeadLineHours: deadlineOffsetHours,
      MediaExistingHandling: existingMediaHandlingPolicy,
      SelectedRuleIds: selectedRuleIds,
    });
  }

  private upsertRule(
    rule: BatchRule,
    detachRelatedSubtitles?: boolean,
  ): Promise<number> {
    return this.httpService
      .post("MediaAssociation/CheckNewRule", {
        RuleId: rule.id,
        RuleName: rule.name,
        IdFormat: rule.idFormat,
        RuleFormat: rule.format,
        MediaFileType: toMediaFileType(rule.type),
        LanguageCode: getLanguageCode(rule),
        SubtitlesFPS: rule.subtitlesFps || undefined,
        SubtitlesOffset: rule.subtitlesOffset || undefined,
        SubtitleRuleHandling: detachRelatedSubtitles ? 0 : 1,
      })
      .then((result) => {
        return result.ruleId;
      });
  }

  private createSummaryResult(apiResult): CheckResultEntry[] {
    const mediaList = apiResult.mediaList;
    const result = [];

    mediaList.forEach((x) => {
      let status = "Unrecognized rule";
      let ruleName = "";
      if (x.Rule) {
        status = "Recognized";
        ruleName = x.Rule.RuleName;

        if (!x.MainMovieUniqueId) {
          status = "Unrecognized ID FORMAT";
        }
        if (x.MediaAssociationNotes && x.MediaAssociationNotes === 0) {
          status = "Recognized without movie";
        }
        if (x.ExistingMediaId) {
          status = "Recognized with existing VIDEO ID";
        }
      }
      const isAssociated = !x.MediaAssociationNotes;
      result.push(
        new CheckResultEntry(
          x.FileName,
          x.FullPath,
          status,
          ruleName,
          x.MainMovieUniqueId,
          x.AssociateMediaId,
          isAssociated,
          x.ExistingMediaId,
        ),
      );
    });

    return result;
  }
}
